import Tag, { type TagColor } from '@atlaskit/tag';
import { token } from '@atlaskit/tokens';
import {
  TagColorPalette,
  TagColorPaletteADSRefresh,
  type ColorPalette,
} from '@databases/color-picker/palettes';
import { messages } from '@databases/intl/messages';
import { type SelectOption } from '@databases/types/database';
import {
  mapColorToNewPalette,
  mapDarkColorToLight,
} from '@databases/utils/database/field-types/select';
import React, {
  type FunctionComponent,
  type MouseEventHandler,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

const Container = styled.span<{
  noMargin?: boolean;
  color: string;
  overrideTagStyles?: boolean;
  palette: ColorPalette;
  useNewStyle?: boolean;
  showDisabledValue?: boolean;
}>`
  display: inline-flex;
  max-width: 100%;

  && > * {
    margin: ${(props) => (props.noMargin ? '0' : '4px')};
  }

  &&& button {
    color: inherit;
  }

  &&& > span {
    ${(props) =>
      props.overrideTagStyles && props.color
        ? `
      background-color: ${props.palette.backgroundColor(props.color)};
      color: ${props.palette.textColor(props.color)};
      opacity: ${props.showDisabledValue ? 0.4 : 1};
      border-color: ${props.palette.borderColor(props.color)};`
        : ''}
    > span {
      padding-top: ${token('space.025', '0.125rem')};
      padding-bottom: ${token('space.025', '0.125rem')};
      ${(props) =>
        props.useNewStyle
          ? `font: ${token('font.body')};`
          : `font-weight: bold;
      font-size: 11px;
      line-height: 16px;
      text-transform: uppercase;`}
      ${(props) =>
        props.transformLabelUppercase === false ? '' : `text-transform: uppercase;`}
      max-width: 100%;
    }
  }
`;

interface SelectOptionTagProps {
  option: SelectOption | undefined;
  isRemovable?: boolean;
  onRemove?: MouseEventHandler;
  noMargin?: boolean;
  showDisabledValue?: boolean;
}

/**
 * Renders a single option of an Databases select field, styled as an @atlaskit/tag component.
 */
const SelectOptionTag: FunctionComponent<SelectOptionTagProps> = ({
  option,
  isRemovable = false,
  onRemove,
  noMargin = false,
  showDisabledValue = false,
}) => {
  const { formatMessage } = useIntl();
  const isDeletedRef = useRef(false);

  const handleBeforeRemove = useCallback(() => {
    isDeletedRef.current = true;
    return false;
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // A very hacky implementation to prevent the mousedown event from bubbling in case the remove icon was clicked,
    // to prevent it from toggling the open state of the SelectPicker.
    // Unfortunately, the mousedown event is called before onBeforeRemoveAction (which reacts to the click event),
    // so isDeletedRef.current is still false here.
    if (e.target instanceof Element && e.target.closest('[aria-label="close tag"]')) {
      e.stopPropagation();
      e.preventDefault();
    }
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (isDeletedRef.current) {
        onRemove?.(e);
        isDeletedRef.current = false;
      }
    },
    [onRemove]
  );

  const useADSRefreshStyle = isFeatureFlagEnabled('platform-component-visual-refresh');
  const useDarkToLightMapping = isFeatureFlagEnabled(
    'platformLozengeTagBadgeVisualUplifts'
  );
  const palette = useMemo(
    () => (useADSRefreshStyle ? TagColorPaletteADSRefresh : TagColorPalette),
    [useADSRefreshStyle]
  );

  const displayColor = useMemo(() => {
    if (!option?.color) {
      return 'standard';
    }
    const color = useDarkToLightMapping
      ? mapDarkColorToLight(option.color)
      : option.color;
    return mapColorToNewPalette(color) as TagColor;
  }, [option?.color, useDarkToLightMapping]);

  return (
    <Container
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      noMargin={noMargin}
      palette={palette}
      overrideTagStyles={!useDarkToLightMapping}
      color={option?.color}
      transformLabelUppercase={useADSRefreshStyle === false}
      useNewStyle={useADSRefreshStyle}
      showDisabledValue={showDisabledValue}
    >
      <Tag
        text={
          option ? option.label : formatMessage(messages['board-layout.empty-tag-label'])
        }
        isRemovable={isRemovable}
        onBeforeRemoveAction={handleBeforeRemove}
        testId={`database-select-option-tag-${
          option ? option.label.replace(/\s+/g, '-').toLowerCase() : 'undefined'
        }`}
        color={displayColor}
      />
    </Container>
  );
};

export default SelectOptionTag;
